import { useMutation } from '@apollo/client';
import { useCallback } from 'react';

import type { AdV5, ScheduleV5, UpdateAdMutation, UpdateAdMutationVariables } from '../../../apis/graphql';
import { CacheEntityIdentifier } from '../../../configs/cache';
import { useTraffickingTableReload } from '../../../contexts/TraffickingTableReloadContext';
import type { Nullable } from '../../../models';
import { useCellVariant } from '../../../pages/Trafficking/TraffickingPage/hooks';
import { AD_TRAFFICKING_COLUMN } from '../../../pages/Trafficking/TraffickingPage/hooks/fragments';
import { UPDATE_TRAFFICKING_PAGE_AD } from '../../../pages/Trafficking/TraffickingPage/hooks/mutations';
import { validateAdSchedule } from '../../AdForm/validation/schedule';
import type { EditableInputVariant } from '../../EditableTableCells/EditableCell/EditableCell';
import type { AdFormErrorType } from '../../utils';
import {
  convertToDateRangeInput,
  convertToDayPartInput,
  getOldCacheData,
  updateTraffickingColumnCache,
} from '../../utils';

export type UpdateAdSchedule = {
  schedule: ScheduleV5;
  lineItemSchedule: Nullable<ScheduleV5>;
};

export type UseUpdateAdScheduleProps = {
  adId: string;
  onSuccessUpdate: () => void;
  onFailureUpdate: (error: AdFormErrorType) => void;
};

export type UseUpdateAdScheduleReturnedType = {
  cellVariant: EditableInputVariant;
  loading: boolean;
  updateAdSchedule: ({ schedule, lineItemSchedule }: UpdateAdSchedule) => Promise<void>;
};

export const useUpdateAdSchedule = ({
  adId,
  onSuccessUpdate,
  onFailureUpdate,
}: UseUpdateAdScheduleProps): UseUpdateAdScheduleReturnedType => {
  const [updateAd, mutationResult] = useMutation<UpdateAdMutation, UpdateAdMutationVariables>(
    UPDATE_TRAFFICKING_PAGE_AD
  );
  const { scheduleTableReload } = useTraffickingTableReload();

  const cellVariant = useCellVariant(mutationResult);
  // we need this data because after update we can't receive some fields from campaign-manager
  // according to bug AX-3785
  const { cacheData, cacheId } = getOldCacheData<AdV5>({
    cacheIdPrefix: CacheEntityIdentifier.AdV5,
    id: adId,
    fragment: AD_TRAFFICKING_COLUMN,
  });

  const updateAdSchedule = useCallback(
    async ({ schedule, lineItemSchedule }: UpdateAdSchedule): Promise<void> => {
      const error = validateAdSchedule(schedule, lineItemSchedule);

      if (error) {
        return onFailureUpdate({ description: error.description, message: error.message });
      }

      try {
        await updateAd({
          variables: {
            id: adId,
            updateAdInput: {
              timezone: schedule?.timezone,
              dateRangeList: convertToDateRangeInput(schedule?.dateRangeList || []),
              dayPartList: convertToDayPartInput(schedule?.dayPartList || []),
              deliveryInViewerTime: schedule?.deliveryInViewerTime,
            },
          },
          update(_, { data }) {
            updateTraffickingColumnCache({
              cacheId,
              data,
              displayPublisherTarget: cacheData.displayPublisherTarget ?? [],
              countriesTarget: cacheData.countriesTarget ?? [],
            });
          },
        });
        onSuccessUpdate();
        // Schedule a delayed refetch so the searcher-applied table (sort order, filters,
        // searcher-only fields) is reconciled after a schedule change.
        scheduleTableReload();
      } catch (e) {
        onFailureUpdate({ message: 'Ad Save Failed', description: (e as Error).message });
      }
    },
    [
      adId,
      cacheId,
      cacheData.displayPublisherTarget,
      cacheData.countriesTarget,
      onFailureUpdate,
      onSuccessUpdate,
      scheduleTableReload,
      updateAd,
    ]
  );

  return {
    cellVariant,
    updateAdSchedule,
    loading: mutationResult.loading,
  };
};
